<template>
    <div class="form-group">
        <label for="email">Primary email address</label> <span class="badge-req badge badge-danger">Required</span>
        <p><span v-if="!isValid" class="text-danger">{{invalidMessage}}</span></p>
        <multiselect
            v-model="selectedEmailOption"
            :options="userOptions"
            placeholder="Search for users or enter a new email address"
            label="email"
            track-by="email"
            tag-placeholder="Use this as new email"
            :taggable="true" 
            :searchable="true" 
            :loading="isLoading" 
            :internal-search="false"
            @tag="addEmail"
            @search-change="asyncFind"
        >
        </multiselect>
    </div>
</template>

<script>

import { User } from 'ca-api-client';
import { validateEmail } from '@/helper/email';
import Multiselect from 'vue-multiselect';

import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
    name: 'EmailSearch',
    components: {
        Multiselect
    },
    props: {
        value: String,
        context: String,
    },
    data() {
        return {
            email: this.value,
            tagOption: this.value ? {
                email: this.email
            } : null,
            selectedEmailOption: this.tagOption,
            userOptions: [],
            isValid: Boolean(this.value),
            invalidMessage: "An email address is required",
            isLoading: false,
        }
    },
    computed:{
        allUserOptions() {
            if (this.tagOption) {
                return [
                    ...this.userOptions,
                    this.tagOption
                ];
            }
            return this.userOptions;
        }
    },
    methods: {
        addEmail(email) {
            let emailOption = {
                email
            };

            this.tagOption = emailOption;
            this.selectedEmailOption = emailOption;

            if (! validateEmail(email)) {
                this.isValid = false;
                this.invalidMessage = "Your email is invalid";
            }
            else {
                let userAlreadyExists = false;
                let validExistingUser = null;
                // Search for users with this email, including super users and soft-deleted (trashed) users.
                // Note: If the current user does not have access to the user, the API will return only the uuid of the user.
                let promise1 = User
                    .where('email', encodeURIComponent(email))
                    .where('include_super_users', 1)
                    .where('include_trashed', 1)
                    .get().then((users) => {
                        if (users.data.length > 0) {
                            userAlreadyExists = true;
                            if(users.data[0].trashed) {
                                validExistingUser = users.data[0];
                            }
                        }
                });
                let promises = [promise1];
                // A user is considered a valid existing user if they are not already part of the current client/project
                // Or if they are soft deleted
                if (!validExistingUser) {
                    let promise2 = User
                        .where('email', encodeURIComponent(email));

                    if (this.context == 'project') {
                        promise2 = promise2.where('not_project_related', window.ca.project.uuid)
                            .where('no_client_admins', window.ca.client.uuid);
                    } else if (this.context == 'client') {
                        promise2 = promise2.where('not_client_related', window.ca.client.uuid);
                    }
                    
                    promise2 = promise2
                        .get().then((users) => {
                            if (users.data.length > 0) {
                                validExistingUser = users.data[0];
                            }
                    });

                    promises.push(promise2);
                }

                Promise.all(promises).then(() => {
                    // If the user does not already exist, we can safely assign this email to the new user.
                    if (!userAlreadyExists) {
                        this.$emit('user-select', null);
                        this.$emit('input', email);
                        this.isValid = true;
                    // If the user exists but no valid existing user was found, prevent assigning the email.
                    } else if (validExistingUser == null) {
                        this.isValid = false;
                        this.$emit('input', null);
                        this.invalidMessage = 'This email is taken and cannot be assigned to this client/project';
                    // The user exists and is valid, we can assign the existing user.
                    } else {
                        this.isValid = true;
                        this.$emit('input', email);
                        this.$emit('user-select', validExistingUser);
                    }
                });
            }
        },
        asyncFind (searchText) {
            this.isLoading = true;

            let query = User
                .where('available_to_user', 1)
                .where('include_trashed', 1)
                .where('email_partial', searchText);

            if (this.context === 'client') {
                query = query.where('not_client_related', window.ca.client.uuid)
            } else if (this.context === 'project') {
                query = query.where('not_project_related', window.ca.project.uuid)
                .where('no_client_admins', window.ca.client.uuid);
            }
            
            query
                .params({
                    limit: 10
                })
                .get()
                .then((users) => {
                    this.userOptions = users.data.map(user => {
                        return {
                            id: user.id,
                            email: user.email,
                            name: user.name,
                            additional_emails: user.additional_emails,
                            roles: user.roles,
                        };
                    });
                    this.isLoading = false;
                });
        },
    },
    watch: {
        selectedEmailOption:{
            deep: true,
            handler(newValue) {
                if (!newValue) {
                    this.$emit('input', null);
                    return;
                }

                if (newValue.id) {
                    this.$emit('input', newValue.email);
                    this.isValid = true;
                    this.$emit('user-select', newValue);
                }
            }
        }
    }
}
</script>
